import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FileText, Clock, CheckCircle, XCircle, AlertTriangle,
  Calendar, Users, Brain, ArrowRight, Timer, RefreshCw,
  CalendarCheck, ClipboardList, Activity, Eye, AlertCircle,
  BarChart3
} from 'lucide-react';
import api, { getAttendanceSummary, getAttendance, getEmployees } from '../services/api';
import { getDateLocale } from '../utils/i18nHelpers';
import './AdminDashboard.css';

const ATT_STATUSES = [
  { value: 'present',          label_ru: 'На работе',            label_uz: 'Ishda',                      color: '#22c55e', bg: '#f0fdf4' },
  { value: 'late_excused',     label_ru: 'Опоздал (уважит.)',    label_uz: 'Kechikdi (uzrli)',            color: '#f59e0b', bg: '#fffbeb' },
  { value: 'late_unexcused',   label_ru: 'Опоздал (неуважит.)', label_uz: 'Kechikdi (uzrsiz)',           color: '#ef4444', bg: '#fef2f2' },
  { value: 'on_errand',        label_ru: 'Отпросился на время',  label_uz: 'Ruxsat olib ketdi',          color: '#8b5cf6', bg: '#f5f3ff' },
  { value: 'vacation',         label_ru: 'В отпуске',            label_uz: "Ta'tilda",                   color: '#3b82f6', bg: '#eff6ff' },
  { value: 'sick_leave',       label_ru: 'На больничном',        label_uz: 'Kasallik varaqasida',        color: '#f59e0b', bg: '#fffbeb' },
  { value: 'maternity',        label_ru: 'Уход за ребёнком',     label_uz: 'Bola parvarishida',          color: '#8b5cf6', bg: '#f5f3ff' },
  { value: 'pregnancy_leave',  label_ru: 'Декретный отпуск',     label_uz: "Homiladorlik ta'tilida",     color: '#ec4899', bg: '#fdf2f8' },
  { value: 'business_trip',    label_ru: 'В командировке',       label_uz: 'Xizmat safarida',            color: '#06b6d4', bg: '#ecfeff' },
  { value: 'absent',           label_ru: 'Отсутствует',          label_uz: 'Kelmagan',                   color: '#ef4444', bg: '#fef2f2' },
  { value: 'not_reported',     label_ru: 'Не отмечен',           label_uz: 'Belgilanmagan',              color: '#94a3b8', bg: '#f1f5f9' },
];

const STATUS_CSS = {
  draft:     'status-draft',
  submitted: 'status-submitted',
  in_review: 'status-review',
  approved:  'status-approved',
  rejected:  'status-rejected',
  archived:  'status-archived',
};

function getStatusBadge(status, t) {
  const cssClass = STATUS_CSS[status] || 'status-draft';
  const label = t(`common:reportStatuses.${status}`, { defaultValue: status });
  return <span className={`status-badge ${cssClass}`}>{label}</span>;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(getDateLocale());
}

function timeAgo(dateStr, t) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('common:timeAgo.justNow');
  if (mins < 60) return t('common:timeAgo.minutesAgo', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('common:timeAgo.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  return t('common:timeAgo.daysAgo', { count: days });
}

function AdminDashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation(['admin', 'common', 'reports']);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Посещаемость
  const [attSummary, setAttSummary] = useState(null);
  const [attDetailStatus, setAttDetailStatus] = useState(null); // какой статус раскрыт
  const [attDetailList, setAttDetailList] = useState([]);
  const [attDetailLoading, setAttDetailLoading] = useState(false);

  const isRu = (localStorage.getItem('language') || 'uz') === 'ru';

  useEffect(() => {
    fetchData();
    fetchAttendance();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(!data);
      setRefreshing(!!data);
      const resp = await api.getDirectorOverview();
      setData(resp.data);
    } catch (error) {
      console.error('Ошибка загрузки:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchAttendance = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const [sumRes, empRes, attRes] = await Promise.all([
        getAttendanceSummary({ date: today }),
        getEmployees({ is_active: true }),
        getAttendance({ date: today }),
      ]);
      const emps = empRes.data.results || empRes.data || [];
      const records = attRes.data.results || attRes.data || [];

      // Собираем полную карту: записи из БД, неотмеченные — "not_reported"
      const map = {};
      records.forEach(r => { map[r.employee] = r.status; });
      const totals = {};
      ATT_STATUSES.forEach(s => { totals[s.value] = 0; });
      emps.forEach(emp => {
        const st = map[emp.id] || 'not_reported';
        totals[st] = (totals[st] || 0) + 1;
      });

      setAttSummary({
        totals,
        totalEmployees: emps.length,
        reportedDepts: sumRes.data?.departments?.length || 0,
        employees: emps,
        recordMap: map,
      });
    } catch (err) {
      console.error('Error loading attendance:', err);
    }
  };

  const showAttDetail = (statusValue) => {
    if (attDetailStatus === statusValue) {
      setAttDetailStatus(null);
      setAttDetailList([]);
      return;
    }
    if (!attSummary) return;
    // Фильтруем сотрудников по статусу
    const list = attSummary.employees.filter(emp => {
      const st = attSummary.recordMap[emp.id] || 'not_reported';
      return st === statusValue;
    });
    setAttDetailStatus(statusValue);
    setAttDetailList(list);
  };

  const handleApprove = async (id) => {
    try {
      await api.approveReport(id);
      fetchData();
    } catch (error) {
      console.error('Ошибка утверждения:', error);
      alert(t('admin:errors.approveError'));
    }
  };

  const handleReject = async (id) => {
    const reason = prompt(t('reports:actions.enterRejectionReason'));
    if (reason) {
      try {
        await api.rejectReport(id, reason);
        fetchData();
      } catch (error) {
        console.error('Ошибка отклонения:', error);
        alert(t('reports:messages.rejectError'));
      }
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!data?.pending_reports) return;
    if (selected.size === data.pending_reports.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(data.pending_reports.map(r => r.id)));
    }
  };

  const handleBulkApprove = async () => {
    if (selected.size === 0) return;
    setBulkLoading(true);
    try {
      const resp = await api.bulkApproveReports([...selected]);
      setSelected(new Set());
      fetchData();
      if (resp.data.total_failed > 0) {
        alert(`${t('admin:bulk.approved')}: ${resp.data.total_approved}, ${t('admin:bulk.failed')}: ${resp.data.total_failed}`);
      }
    } catch (error) {
      console.error('Ошибка массового утверждения:', error);
      alert(t('admin:bulk.approveError'));
    }
    setBulkLoading(false);
  };

  const handleBulkReject = async () => {
    if (!rejectReason.trim()) return;
    setBulkLoading(true);
    try {
      const resp = await api.bulkRejectReports([...selected], rejectReason.trim());
      setSelected(new Set());
      setRejectModalOpen(false);
      setRejectReason('');
      fetchData();
      if (resp.data.total_failed > 0) {
        alert(`${t('admin:bulk.rejected')}: ${resp.data.total_rejected}, ${t('admin:bulk.failed')}: ${resp.data.total_failed}`);
      }
    } catch (error) {
      console.error('Ошибка массового отклонения:', error);
      alert(t('admin:bulk.rejectError'));
    }
    setBulkLoading(false);
  };

  if (loading) {
    return <div className="loading">{t('admin:dashboard.loading')}</div>;
  }

  if (!data) {
    return <div className="loading">{t('admin:dashboard.loadingError')}</div>;
  }

  const { summary, weekly_control, critical_overdue, burning_deadlines,
          problem_departments, ai_recommendations, recent_actions,
          pending_reports } = data;

  const today = new Date().toLocaleDateString(getDateLocale(), {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h1>{t('admin:dashboard.title')}</h1>
          <p>{today}</p>
        </div>
        <button
          className="btn-refresh"
          onClick={fetchData}
          disabled={refreshing}
        >
          <RefreshCw size={16} className={refreshing ? 'spinning' : ''} />
          {t('common:buttons.refresh')}
        </button>
      </div>

      {/* Stat Cards */}
      <div className="stats-grid">
        <div className="stat-card orange">
          <Clock size={24} />
          <div>
            <div className="stat-value">{summary.reports_pending_review}</div>
            <div className="stat-label">{t('admin:dashboard.stats.pendingReview')}</div>
          </div>
        </div>
        <div className="stat-card red">
          <AlertTriangle size={24} />
          <div>
            <div className="stat-value">{summary.documents_overdue}</div>
            <div className="stat-label">{t('admin:dashboard.stats.overdue')}</div>
          </div>
        </div>
        <div className="stat-card blue">
          <BarChart3 size={24} />
          <div>
            <div className="stat-value">{summary.plans_avg_completion}%</div>
            <div className="stat-label">{t('admin:dashboard.stats.plans')}</div>
          </div>
        </div>
        <div className="stat-card green">
          <Users size={24} />
          <div>
            <div className="stat-value">{summary.active_users_today}</div>
            <div className="stat-label">{t('admin:dashboard.stats.onlineToday')}</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <button
          className="quick-action-btn qa-approve"
          onClick={() => {
            const el = document.getElementById('pending-reports');
            if (el) el.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          <CheckCircle size={18} />
          {t('admin:dashboard.actions.approveReports')} ({pending_reports.length})
        </button>
        <button
          className="quick-action-btn qa-weekly"
          onClick={() => navigate('/weekly-control')}
        >
          <CalendarCheck size={18} />
          {t('admin:dashboard.actions.weeklyControl')}
        </button>
        <button
          className="quick-action-btn qa-constructor"
          onClick={() => navigate('/information-constructor')}
        >
          <FileText size={18} />
          {t('admin:dashboard.actions.generateInfo')}
        </button>
      </div>

      {/* Widgets Grid */}
      <div className="director-widgets">
        {/* LEFT COLUMN */}
        <div className="widgets-column">

          {/* 1. Сдача отчётов за неделю */}
          <div className="widget-card">
            <div className="widget-header">
              <h3 className="widget-title">
                <CalendarCheck size={18} />
                {t('admin:dashboard.widgets.weeklySubmission')}
              </h3>
              <span className="widget-period">
                {formatDate(weekly_control.week_start)} — {formatDate(weekly_control.week_end)}
              </span>
            </div>

            <div className="weekly-row">
              <span className="weekly-label">{t('admin:dashboard.weekly.plans')}</span>
              <div className="weekly-progress">
                <div className="progress-track">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${weekly_control.total_departments > 0
                        ? (weekly_control.plans_submitted / weekly_control.total_departments * 100)
                        : 0}%`
                    }}
                  />
                </div>
                <span className="progress-text">
                  {weekly_control.plans_submitted}/{weekly_control.total_departments}
                </span>
              </div>
            </div>

            <div className="weekly-row">
              <span className="weekly-label">{t('admin:dashboard.weekly.reports')}</span>
              <div className="weekly-progress">
                <div className="progress-track">
                  <div
                    className="progress-fill reports-fill"
                    style={{
                      width: `${weekly_control.total_departments > 0
                        ? (weekly_control.reports_submitted / weekly_control.total_departments * 100)
                        : 0}%`
                    }}
                  />
                </div>
                <span className="progress-text">
                  {weekly_control.reports_submitted}/{weekly_control.total_departments}
                </span>
              </div>
            </div>

            {weekly_control.departments_missing_report.length > 0 && (
              <div className="missing-section">
                <div className="missing-header">
                  {t('admin:dashboard.weekly.didNotSubmitReport')} ({weekly_control.departments_missing_report.length}):
                </div>
                <div className="missing-list">
                  {weekly_control.departments_missing_report.slice(0, 8).map(dept => (
                    <div key={dept.id} className="missing-dept">
                      <XCircle size={12} />
                      <span>{dept.name}</span>
                    </div>
                  ))}
                  {weekly_control.departments_missing_report.length > 8 && (
                    <div className="missing-more">
                      {t('admin:dashboard.weekly.andMore', { count: weekly_control.departments_missing_report.length - 8 })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 2. Критические просрочки */}
          <div className="widget-card">
            <div className="widget-header">
              <h3 className="widget-title">
                <AlertTriangle size={18} />
                {t('admin:dashboard.widgets.criticalOverdue')}
              </h3>
              <span className="widget-count red-text">{critical_overdue.length}</span>
            </div>
            {critical_overdue.length === 0 ? (
              <div className="widget-empty green-text">
                <CheckCircle size={20} />
                {t('admin:dashboard.empty.noOverdue')}
              </div>
            ) : (
              <div className="overdue-list">
                {critical_overdue.map(doc => (
                  <div
                    key={doc.id}
                    className="overdue-card"
                    onClick={() => navigate(`/documents/${doc.id}`)}
                  >
                    <div className="overdue-top">
                      <span className="overdue-number">{doc.registration_number}</span>
                      <span className="overdue-badge">-{doc.days_overdue} дн.</span>
                    </div>
                    <div className="overdue-title">{doc.title}</div>
                    <div className="overdue-meta">
                      <span>{doc.department_name}</span>
                      {doc.authority_name && (
                        <span className="overdue-authority">{doc.authority_name}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 3. Отчёты на утверждение */}
          <div className="widget-card" id="pending-reports">
            <div className="widget-header">
              <h3 className="widget-title">
                <ClipboardList size={18} />
                {t('admin:dashboard.widgets.pendingReports')}
              </h3>
              <span className="widget-count">{pending_reports.length}</span>
            </div>
            {pending_reports.length === 0 ? (
              <div className="widget-empty">
                <CheckCircle size={20} />
                {t('admin:dashboard.empty.allProcessed')}
              </div>
            ) : (
              <>
                {/* Панель массовых действий */}
                {selected.size > 0 && (
                  <div className="bulk-panel">
                    <span className="bulk-count">{t('admin:bulk.selected')}: {selected.size}</span>
                    <button
                      className="bulk-btn bulk-approve"
                      onClick={handleBulkApprove}
                      disabled={bulkLoading}
                    >
                      <CheckCircle size={14} />
                      {t('admin:bulk.approveSelected')} ({selected.size})
                    </button>
                    <button
                      className="bulk-btn bulk-reject"
                      onClick={() => setRejectModalOpen(true)}
                      disabled={bulkLoading}
                    >
                      <XCircle size={14} />
                      {t('admin:bulk.rejectSelected')}
                    </button>
                    <button
                      className="bulk-btn bulk-clear"
                      onClick={() => setSelected(new Set())}
                    >
                      {t('admin:bulk.clear')}
                    </button>
                  </div>
                )}

                <div className="reports-table">
                  <table>
                    <thead>
                      <tr>
                        <th className="th-check">
                          <input
                            type="checkbox"
                            checked={selected.size === pending_reports.length && pending_reports.length > 0}
                            onChange={toggleSelectAll}
                          />
                        </th>
                        <th>{t('admin:table.department')}</th>
                        <th>{t('admin:table.period')}</th>
                        <th>{t('admin:table.status')}</th>
                        <th>{t('admin:table.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pending_reports.map(report => (
                        <tr key={report.id} className={selected.has(report.id) ? 'row-selected' : ''}>
                          <td className="td-check">
                            <input
                              type="checkbox"
                              checked={selected.has(report.id)}
                              onChange={() => toggleSelect(report.id)}
                            />
                          </td>
                          <td>{report.department_name}</td>
                          <td>{report.period_name || '—'}</td>
                          <td>{getStatusBadge(report.status, t)}</td>
                          <td>
                            <div className="action-buttons">
                              <button className="btn-view" onClick={() => navigate(`/reports/${report.id}`)}>
                                <Eye size={12} />
                              </button>
                              <button className="btn-approve" onClick={() => handleApprove(report.id)}>
                                <CheckCircle size={12} />
                              </button>
                              <button className="btn-reject" onClick={() => handleReject(report.id)}>
                                <XCircle size={12} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Модал массового отклонения */}
                {rejectModalOpen && (
                  <div className="bulk-modal-overlay" onClick={() => setRejectModalOpen(false)}>
                    <div className="bulk-modal" onClick={e => e.stopPropagation()}>
                      <h3>{t('admin:bulk.rejectCount', { count: selected.size })}</h3>
                      <textarea
                        className="bulk-modal-textarea"
                        placeholder={t('admin:bulk.rejectionReason')}
                        value={rejectReason}
                        onChange={e => setRejectReason(e.target.value)}
                        rows={4}
                      />
                      <div className="bulk-modal-actions">
                        <button
                          className="bulk-btn bulk-reject"
                          onClick={handleBulkReject}
                          disabled={bulkLoading || !rejectReason.trim()}
                        >
                          {bulkLoading ? t('admin:bulk.rejecting') : t('admin:bulk.rejectCount', { count: selected.size })}
                        </button>
                        <button
                          className="bulk-btn bulk-clear"
                          onClick={() => { setRejectModalOpen(false); setRejectReason(''); }}
                        >
                          {t('common:buttons.cancel')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="widgets-column">

          {/* Посещаемость сегодня */}
          {attSummary && (
            <div className="widget-card">
              <div className="widget-header">
                <h3 className="widget-title">
                  <Users size={18} />
                  {isRu ? 'Посещаемость сегодня' : 'Bugungi davomat'}
                </h3>
                <span className="widget-period">
                  {isRu ? `${attSummary.totalEmployees} сотр.` : `${attSummary.totalEmployees} xodim`}
                </span>
              </div>

              {/* Карточки статусов — кликабельные */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                {ATT_STATUSES.filter(st => attSummary.totals[st.value] > 0).map(st => (
                  <div
                    key={st.value}
                    onClick={() => showAttDetail(st.value)}
                    style={{
                      padding: '8px 14px', borderRadius: '10px', cursor: 'pointer',
                      backgroundColor: attDetailStatus === st.value ? st.color : st.bg,
                      color: attDetailStatus === st.value ? '#fff' : st.color,
                      border: `2px solid ${st.color}`,
                      transition: 'all 0.15s', textAlign: 'center', minWidth: '80px',
                    }}
                  >
                    <div style={{ fontSize: '20px', fontWeight: '700', lineHeight: 1.2 }}>
                      {attSummary.totals[st.value]}
                    </div>
                    <div style={{ fontSize: '11px', fontWeight: '500' }}>
                      {isRu ? st.label_ru : st.label_uz}
                    </div>
                  </div>
                ))}
              </div>

              {/* Развёрнутый список при клике */}
              {attDetailStatus && attDetailList.length > 0 && (
                <div style={{
                  backgroundColor: '#f8fafc', borderRadius: '8px', padding: '12px',
                  maxHeight: '280px', overflowY: 'auto',
                }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                    <span>
                      {(() => { const info = ATT_STATUSES.find(s => s.value === attDetailStatus); return isRu ? info?.label_ru : info?.label_uz; })()}
                      {' '}({attDetailList.length})
                    </span>
                    <span
                      style={{ cursor: 'pointer', color: '#94a3b8' }}
                      onClick={() => { setAttDetailStatus(null); setAttDetailList([]); }}
                    >✕</span>
                  </div>
                  {attDetailList.map(emp => (
                    <div key={emp.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '6px 0', borderBottom: '1px solid #e2e8f0', fontSize: '13px',
                    }}>
                      <div>
                        <div style={{ fontWeight: '500', color: '#1e293b' }}>{emp.full_name}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{emp.position || '—'}</div>
                      </div>
                      <div style={{ fontSize: '11px', color: '#64748b', textAlign: 'right' }}>
                        {isRu ? emp.department_name : (emp.department_name_uz || emp.department_name)}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Инфо о непоступивших отделах */}
              {attSummary.reportedDepts < 30 && (
                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
                  {isRu
                    ? `Отчитались: ${attSummary.reportedDepts} отделов. Не отмеченные отображаются серым.`
                    : `Hisobot: ${attSummary.reportedDepts} bo'lim. Belgilanmaganlar kulrang rangda.`}
                </div>
              )}
            </div>
          )}

          {/* 4. Горящие дедлайны */}
          <div className="widget-card">
            <div className="widget-header">
              <h3 className="widget-title">
                <Timer size={18} />
                {t('admin:dashboard.widgets.burningDeadlines')}
              </h3>
              <span className="widget-period">{t('admin:dashboard.deadlines.next7days')}</span>
            </div>
            {burning_deadlines.length === 0 ? (
              <div className="widget-empty">
                <Calendar size={20} />
                {t('admin:dashboard.empty.noDeadlines')}
              </div>
            ) : (
              <div className="deadline-list">
                {burning_deadlines.map((item, idx) => (
                  <div
                    key={`${item.type}-${item.id}`}
                    className="deadline-card"
                    onClick={() => {
                      if (item.type === 'document') navigate(`/documents/${item.id}`);
                      else if (item.plan_id) navigate(`/plans/${item.plan_id}`);
                    }}
                  >
                    <div className="deadline-top">
                      {item.type === 'document'
                        ? <FileText size={14} className="deadline-icon-doc" />
                        : <ClipboardList size={14} className="deadline-icon-plan" />
                      }
                      <span className="deadline-title">{item.title}</span>
                      <span className={`deadline-badge ${item.days_remaining <= 1 ? 'critical' : ''}`}>
                        {item.days_remaining === 0 ? t('admin:dashboard.deadlines.today') : t('admin:dashboard.deadlines.daysLeft', { days: item.days_remaining })}
                      </span>
                    </div>
                    <div className="deadline-meta">
                      <span>{item.department_name}</span>
                      <span>{formatDate(item.deadline)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 5. Проблемные отделы */}
          <div className="widget-card">
            <div className="widget-header">
              <h3 className="widget-title">
                <AlertCircle size={18} />
                {t('admin:dashboard.widgets.problemDepartments')}
              </h3>
              <button
                className="widget-link"
                onClick={() => navigate('/analytics')}
              >
                {t('admin:dashboard.details')} <ArrowRight size={14} />
              </button>
            </div>
            {problem_departments.length === 0 ? (
              <div className="widget-empty green-text">
                <CheckCircle size={20} />
                {t('admin:dashboard.empty.allNormal')}
              </div>
            ) : (
              <div className="problem-list">
                {problem_departments.map(dept => {
                  const kpiColor = dept.kpi_score < 30 ? 'kpi-red'
                    : dept.kpi_score < 60 ? 'kpi-orange' : 'kpi-yellow';
                  return (
                    <div key={dept.department_id} className="problem-row">
                      <div className="problem-info">
                        <span className="problem-rank">#{dept.rank}</span>
                        <span className="problem-name">{dept.department_name}</span>
                      </div>
                      <div className="problem-kpi">
                        <div className="kpi-bar-track">
                          <div
                            className={`kpi-bar-fill ${kpiColor}`}
                            style={{ width: `${Math.min(dept.kpi_score, 100)}%` }}
                          />
                        </div>
                        <span className={`kpi-score ${kpiColor}`}>{dept.kpi_score}</span>
                      </div>
                      {dept.issues.length > 0 && (
                        <div className="problem-issues">
                          {dept.issues.map((issue, i) => (
                            <span key={i} className="issue-tag">{issue}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 6. Рекомендации ИИ */}
          <div className="widget-card">
            <div className="widget-header">
              <h3 className="widget-title">
                <Brain size={18} />
                {t('admin:dashboard.widgets.aiRecommendations')}
              </h3>
              <button
                className="widget-link"
                onClick={() => navigate('/ai-assistant')}
              >
                {t('admin:dashboard.all')} <ArrowRight size={14} />
              </button>
            </div>
            {ai_recommendations.length === 0 ? (
              <div className="widget-empty">
                <Brain size={20} />
                {t('admin:dashboard.empty.noRecommendations')}
              </div>
            ) : (
              <div className="recommendations-list">
                {ai_recommendations.map(rec => (
                  <div key={rec.id} className="recommendation-card">
                    <div className="rec-header">
                      <span className={`rec-priority priority-${rec.priority}`}>
                        {t(`admin:dashboard.priority.${rec.priority}`, { defaultValue: rec.priority })}
                      </span>
                      <span className="rec-dept">{rec.department_name}</span>
                    </div>
                    <div className="rec-title">{rec.title}</div>
                    {rec.content && (
                      <div className="rec-content">{rec.content}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 7. Лента событий */}
          <div className="widget-card">
            <div className="widget-header">
              <h3 className="widget-title">
                <Activity size={18} />
                {t('admin:dashboard.widgets.eventFeed')}
              </h3>
              <button
                className="widget-link"
                onClick={() => navigate('/audit-log')}
              >
                {t('admin:dashboard.journal')} <ArrowRight size={14} />
              </button>
            </div>
            {recent_actions.length === 0 ? (
              <div className="widget-empty">{t('admin:dashboard.empty.noActions')}</div>
            ) : (
              <div className="timeline">
                {recent_actions.map((action, idx) => (
                  <div key={idx} className="timeline-item">
                    <div className={`timeline-dot dot-${action.action}`} />
                    <div className="timeline-content">
                      <div className="timeline-header">
                        <span className="timeline-user">{action.user_name}</span>
                        <span className="timeline-time">{timeAgo(action.created_at, t)}</span>
                      </div>
                      <div className="timeline-desc">
                        {action.action_display}: {action.description || action.model_name}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
